import { useState, useEffect } from 'react';
import { Users, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { Match } from '../api/matchesApi';
import { format } from 'date-fns';
import { formatRound } from '../utils/general';
import MatchDetailsTabs from './MatchDetailsTabs';

interface MatchCardProps {
  match: Match;
  onPlaceBet?: (matchId: string, homeScore: string, awayScore: string, winnerId?: number | null) => Promise<void> | void;
  compact?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, onPlaceBet, compact = false }) => {
  const [homeScore, setHomeScore] = useState<string>('');
  const [awayScore, setAwayScore] = useState<string>('');
  const [qualifierTeamId, setQualifierTeamId] = useState<number | null | undefined>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  
  const homeTeamName = match.home_team?.name || match.home_team_placeholder || '';
  const awayTeamName = match.away_team?.name || match.away_team_placeholder || '';
  const homeTeamLogo = match.home_team?.logo_url || '';
  const awayTeamLogo = match.away_team?.logo_url || '';
  
  const isUpcoming = match.status === 'SCHEDULED';
  const isLive = match.status === 'LIVE';
  const isCompleted = match.status === 'COMPLETED';
  const isMatchStarted = isLive || isCompleted;
  
  const hasUserBet = match.user_bet_home_score !== undefined && 
                    match.user_bet_away_score !== undefined;
  
  const isKnockout = match.round !== "GROUP";
  
  useEffect(() => {
    if (hasUserBet && match.user_bet_home_score !== null && match.user_bet_away_score !== null && 
        match.user_bet_home_score !== undefined && match.user_bet_away_score !== undefined) {
      setHomeScore(match.user_bet_home_score.toString());
      setAwayScore(match.user_bet_away_score.toString());
      setQualifierTeamId(match.user_bet_winner);
    }
  }, [match, hasUserBet]);
  
  // Auto-save function
  const saveBet = async (home: string, away: string, qualifier: number | null | undefined) => {
    if (!match.bettable || !onPlaceBet) return;

    const homeScoreNum = parseInt(home, 10);
    const awayScoreNum = parseInt(away, 10);

    if (isNaN(homeScoreNum) || isNaN(awayScoreNum)) return;

    let winnerId = null;
    if (isKnockout) {
      if (homeScoreNum === awayScoreNum) {
        // Don't submit if it's a draw in knockout and no winner is picked
        if (!qualifier) return;
        winnerId = qualifier;
      } else {
        winnerId = homeScoreNum > awayScoreNum ? match.home_team_id : match.away_team_id;
      }
    } else {
      if (homeScoreNum !== awayScoreNum) winnerId = homeScoreNum > awayScoreNum ? match.home_team_id : match.away_team_id;
    }

    try {
      await onPlaceBet(match.id, home, away, winnerId ?? null);
      setShowSaveIndicator(true);
      setTimeout(() => setShowSaveIndicator(false), 2000);
    } catch {
      window.location.reload();
    }
  };

  // Handle home score change
  const handleHomeScoreChange = (value: string) => {
    setHomeScore(value);
    setQualifierTeamId(null);
    if (value !== '' && awayScore !== '') {
      saveBet(value, awayScore, null);
    }
  };

  // Handle away score change
  const handleAwayScoreChange = (value: string) => {
    setAwayScore(value);
    setQualifierTeamId(null);
    if (homeScore !== '' && value !== '') {
      saveBet(homeScore, value, null);
    }
  };

  // Handle qualifier change
  const handleQualifierChange = (teamId: number | null) => {
    setQualifierTeamId(teamId);
    if (homeScore !== '' && awayScore !== '') {
      saveBet(homeScore, awayScore, teamId);
    }
  };

  function renderMatchScore(homeScore: number | null | undefined, awayScore: number | null | undefined, homeTeamId: number | null | undefined, awayTeamId: number | null | undefined, winnerId: number | null | undefined) {
    if (homeScore == null || awayScore == null) return <span>- : -</span>;
    const isDraw = homeScore === awayScore;
    const winnerClasses = 'underline decoration-green-600 decoration-2 underline-offset-4';
    return (
      <span>
        <span className={(isDraw && winnerId === homeTeamId) ? winnerClasses : ''}>{homeScore}</span>
        {' : '}
        <span className={(isDraw && winnerId === awayTeamId) ? winnerClasses: ''}>{awayScore}</span>
      </span>
    );
  }
  
  function renderUserPrediction(homeScore: number | null | undefined, awayScore: number | null | undefined, homeTeamId: number | null | undefined, awayTeamId: number | null | undefined, predictedWinnerId: number | null | undefined) {
    if (homeScore == null || awayScore == null) return <span>- : -</span>;
    const isDraw = homeScore === awayScore;
    const winnerClasses = 'underline decoration-grey-400 decoration-2 underline-offset-4';
    return (
      <span>
        <span className={isDraw && predictedWinnerId === homeTeamId ? winnerClasses : undefined}>{homeScore}</span>
        {' : '}
        <span className={isDraw && predictedWinnerId === awayTeamId ? winnerClasses : undefined}>{awayScore}</span>
      </span>
    );
  }

  // Compact mode sizing adjustments
  const logoSize = compact ? 'w-8 h-8' : 'w-12 h-12';
  const logoInnerSize = compact ? 'w-7 h-7' : 'w-10 h-10';
  const teamNameSize = compact 
  ? 'text-xs md:text-sm md:font-bold max-w-[80px] md:max-w-[100px]' 
  : 'text-sm md:text-xl md:font-bold max-w-[80px] md:max-w-[130px]';
  const mainContainerHeight = compact ? 'h-[45px]' : 'min-h-[112px] h-[112px]';
  const inputSize = compact ? 'w-6 h-7 text-base' : 'w-10 h-10 text-lg';
  const scoreSize = compact ? 'text-m' : 'text-2xl';
  const marginY = compact ? 'my-1' : 'my-2';
  const badgeTextSize = compact ? 'text-base' : 'text-lg';
  const badgePointsTextSize = compact ? 'text-xs' : 'text-base';

  return (
    <div className={`relative w-full max-w-lg mx-auto ${marginY}`}>
      <div 
        className={`bg-gray-900 text-white rounded-lg w-full border border-gray-800 overflow-hidden transition-colors ${isMatchStarted ? 'cursor-pointer hover:bg-gray-800/40' : ''}`}
        dir="rtl"
        onClick={() => isMatchStarted && setShowDetails(!showDetails)}
      >
        <style>{`
          .no-arrows::-webkit-outer-spin-button,
          .no-arrows::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .no-arrows {
            -moz-appearance: textfield;
          }
        `}</style>
        
        <div className="relative">
          {/* GAME STATE: Absolute positioned to top right */}
          {isMatchStarted && (
            <div className="absolute top-0 right-0 z-10 flex items-center">
              <div className={`
                flex items-center gap-1.5 px-2 py-0.5 
                font-bold
                ${isLive ? 'text-red-500' : 'text-gray-500'} 
                ${compact ? 'text-[10px]' : 'text-xs'}
              `}>
                {isLive && (
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                )}
                <span>{isLive ? 'ישיר' : 'הסתיים'}</span>
              </div>
            </div>
          )}
          
          <div className={`flex flex-row items-stretch justify-center w-full ${mainContainerHeight}`}>
            
            {/* SECTION 2: Home Team */}
            <div className="flex flex-row items-center justify-end flex-1 min-w-0 gap-1">
              <div className={`text-center ${compact ? 'mr-1' : 'mr-2'}`}>
                <div className={teamNameSize}>{homeTeamName}</div>
              </div>
              <div className={logoSize} hidden={!homeTeamLogo}>
                <div 
                  className={`w-full h-full rounded-full flex items-center justify-center relative overflow-hidden cursor-pointer transition-all duration-300 ${
                    isKnockout && !isMatchStarted && homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) === parseInt(awayScore, 10) && !qualifierTeamId
                      ? 'ring-2 ring-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.6)] animate-pulse'
                      : ''
                  } ${
                    !isMatchStarted && qualifierTeamId === match.home_team_id && parseInt(homeScore, 10) === parseInt(awayScore, 10)
                      ? 'ring-2 ring-green-500'
                      : ''
                  }`}
                  onClick={() => {
                    if (isKnockout && !isMatchStarted && homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) === parseInt(awayScore, 10)) {
                      handleQualifierChange(match.home_team_id ?? null);
                    }
                  }}
                >
                  {homeTeamLogo && (
                    <img 
                      src={homeTeamLogo} 
                      alt={`${homeTeamName} logo`} 
                      className={`${logoInnerSize} object-cover rounded-full`}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 3: Match Details & Betting */}
            <div className={`flex flex-col items-center px-2 shrink-0 justify-center ${compact ? 'w-17' : 'w-36'}`}>
              {isMatchStarted ? (
                <>
                  <div className={`${scoreSize} text-white text-center font-bold`}>
                    {renderMatchScore(match.home_team_score, match.away_team_score, match.home_team_id, match.away_team_id, match.winner)}
                  </div>
                  <div className={`text-center ${compact ? 'pb-1' : 'pb-2'} text-gray-400 ${compact ? 'text-xs' : 'text-base'}`}>
                    {renderUserPrediction(match.user_bet_home_score, match.user_bet_away_score, match.home_team_id, match.away_team_id, match.user_bet_winner)}
                  </div>
                </>
              ) : !match.bettable ? (
                /* New condition: Match hasn't started but is not open for betting */
                <div className="flex flex-col items-center justify-center text-center">
                  <span className={`text-gray-500 font-medium leading-tight ${compact ? 'text-[12px]' : 'text-sm'}`}>
                    טרם נפתח
                  </span>
                </div>
              ) : (
                <>
                  <div className={`flex items-center justify-center ${compact ? 'gap-2' : 'gap-3'} ${compact ? 'min-h-[32px]' : 'min-h-[48px]'} relative`}>
                    <input
                      type="number"
                      min="0"
                      className={`${inputSize} bg-gray-800 text-center rounded-md border border-gray-700 font-bold no-arrows transition-all duration-200`}
                      value={homeScore}
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.currentTarget.select()}
                      onChange={(e) => handleHomeScoreChange(e.target.value)}
                      placeholder="?"
                    />
                    
                    {showSaveIndicator && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                        <div className="bg-green-500 text-white rounded-full p-0.5 shadow-lg animate-bounce">
                          <svg className={`${compact ? 'w-2 h-2' : 'w-3 h-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                    
                    <input
                      type="number"
                      min="0"
                      className={`${inputSize} bg-gray-800 text-center rounded-md border border-gray-700 font-bold no-arrows transition-all duration-200`}
                      value={awayScore}
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.currentTarget.select()}
                      onChange={(e) => handleAwayScoreChange(e.target.value)}
                      placeholder="?"
                    />
                  </div>
                  {isKnockout && homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) === parseInt(awayScore, 10) && !qualifierTeamId && (
                    <span className={`text-yellow-400 ${compact ? 'text-[10px]' : 'text-xs'} font-medium`}>
                      בחר מי תעלה
                    </span>
                  )}
                </>
              )}
            </div>

            {/* SECTION 4: Away Team */}
            <div className="flex flex-row items-center justify-start flex-1 min-w-0 gap-1">
              <div className={logoSize} hidden={!awayTeamLogo}>
                <div 
                  className={`w-full h-full rounded-full flex items-center justify-center relative overflow-hidden cursor-pointer transition-all duration-300 ${
                    isKnockout && !isMatchStarted && homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) === parseInt(awayScore, 10) && !qualifierTeamId
                      ? 'ring-2 ring-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.6)] animate-pulse'
                      : ''
                  } ${
                    !isMatchStarted && qualifierTeamId === match.away_team_id && parseInt(homeScore, 10) === parseInt(awayScore, 10)
                      ? 'ring-2 ring-green-500'
                      : ''
                  }`}
                  onClick={() => {
                    if (isKnockout && !isMatchStarted && homeScore !== '' && awayScore !== '' && parseInt(homeScore, 10) === parseInt(awayScore, 10)) {
                      handleQualifierChange(match.away_team_id ?? null);
                    }
                  }}
                >
                  {awayTeamLogo && (
                    <img 
                      src={awayTeamLogo} 
                      alt={`${awayTeamName} logo`} 
                      className={`${logoInnerSize} object-cover rounded-full`}
                    />
                  )}
                </div>
              </div>
              <div className={`text-center`}>
                <div className={teamNameSize}>{awayTeamName}</div>
              </div>
            </div>

            {/* SECTION 1: Points/Time Badge Column */}
            <div className={`flex flex-col items-center justify-center shrink-0 ${compact ? 'w-12' : 'w-16'} border-l border-r border-gray-800 bg-gray-800/20`}>
              {isMatchStarted ? (
                hasUserBet && (
                  <div className="flex flex-col items-center">
                    {(() => {
                      const textColor = 
                        match.points != null && match.points > 0
                          ? (match.score_success && match.result_success)
                            ? 'text-green-500'
                            : 'text-blue-500'
                          : 'text-gray-500';

                      return (
                        <>
                          <div className="flex flex-row items-baseline gap-0.5">
                            <span className={`text-white ${badgeTextSize}`}>
                              {match.points || 0}
                            </span>
                            <span className={`text-white ${badgePointsTextSize} font-bold opacity-90`}>
                              נק׳
                            </span>
                          </div>
                            
                          <span className={`${textColor} ${compact ? 'text-[12px]' : 'text-xs'} font-bold leading-tight`}>
                            {(() => {
                              if (!match.points || match.points === 0) return 'כלום';
                              if (isKnockout) {
                                if (match.score_success && match.result_success) return 'מושלם';
                                if (match.score_success) return 'תוצאה';
                                if (match.result_success) return 'עולה';
                                return 'כלום';
                              } else {
                                if (match.score_success) return 'מושלם';
                                if (match.result_success) return 'כיוון';
                                return 'כלום';
                              }
                            })()}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center leading-tight">
                  <span className={`${compact ? 'text-xs' : 'text-sm'} text-gray-400 font-bold`}>
                    {new Date(match.match_date).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })}
                  </span>
                  <span className={`${compact ? 'text-xs' : 'text-sm'} text-white font-bold`}>
                    {new Date(match.match_date).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
{/* Expanded Details Section */}
{isMatchStarted && showDetails && (
        <div className="border-t border-gray-800" onClick={(e) => e.stopPropagation()}>
          <MatchDetailsTabs
            matchId={match.id}
            isGroupMatch={!isKnockout}
            groupId={match.group_id}
            homeTeamId={match.home_team_id}
            awayTeamId={match.away_team_id}
          />
        </div>
      )}
      </div>
    </div>
  );
};

export default MatchCard;